import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '@/i18n/useTranslation'
import {
  CONSENT_EVENT,
  CONSENT_STORAGE_KEY,
  type CookieConsent,
} from '@/lib/cookieConsent'

/**
 * Lightweight marketing-site cookie notice. Essential cookies always allowed;
 * accepting enables remembering the preference (and future non-essential use).
 */
export default function CookieNotice() {
  const { t } = useTranslation('common')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
      if (!stored) setVisible(true)
    } catch {
      setVisible(true)
    }
  }, [])

  const save = (value: CookieConsent) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, value)
    } catch {
      /* ignore */
    }
    try {
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }))
    } catch {
      /* ignore */
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label={t('marketing.cookies.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur sm:p-5"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-slate-700">
          <p className="font-medium text-slate-900">{t('marketing.cookies.title')}</p>
          <p className="mt-1 text-slate-600">
            {t('marketing.cookies.body')}{' '}
            <Link to="/legal/privacy" className="text-indigo-600 underline-offset-2 hover:underline">
              {t('marketing.footer.privacy')}
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => save('essential')}
          >
            {t('marketing.cookies.essentialOnly')}
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            onClick={() => save('accepted')}
          >
            {t('marketing.cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
